import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";
import {
  useGetConnectors,
  useCreateConnector,
  useEditConnector,
  useDeleteConnector,
} from "../../../hooks/useConnector";
import { useSites } from "../../../hooks/useSite";
import type { IConnectorRow, ConnectorSecret } from "../../../components/connectors/models/Connector";
import type ConnectorRuleSet from "../../../components/connectors/models/ConnectorRuleSet";
import type ConnectorRule from "../../../components/connectors/models/ConnectorRule";
import type Site from "../../../types/site/Site";
import {
  toolbarBtnSx,
  flyoutInputSx,
  flyoutLabelSx,
  flyoutSelectSx,
  crudTableSx,
  selectedRowSx,
  radioInputSx,
  primaryCellSx,
  CrudPageLayout,
  LoadingState,
  EmptyState,
  FlyoutDrawer,
  FlyoutFooter,
  DeleteConfirmation,
  PlusIcon,
} from "../../components/shared";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------


const CONNECTOR_TYPES = ["CXone", "AmazonConnect", "Five9"] as const;

const INTERVAL_OPTIONS = [
  { label: "1 day", value: 86_400 },
  { label: "1 week", value: 604_800 },
  { label: "2 weeks", value: 1_209_600 },
  { label: "30 days", value: 2_592_000 },
];

function makeFormatInterval(t: (key: string, opts?: Record<string, unknown>) => string) {
  return function formatInterval(seconds: number): string {
    const match = INTERVAL_OPTIONS.find((o) => o.value === seconds);
    if (match) return match.label;
    if (seconds < 3600) return `${Math.round(seconds / 60)} ${t("connectors:min")}`;
    if (seconds < 86_400) return `${Math.round(seconds / 3600)} ${t("connectors:hr")}`;
    const days = Math.round(seconds / 86_400);
    return `${days} ${days === 1 ? t("connectors:day") : t("connectors:days")}`;
  };
}

// ---------------------------------------------------------------------------
// Type-specific field definitions
// ---------------------------------------------------------------------------

interface FieldDef {
  key: string;
  label: string;
  required: boolean;
  sensitive: boolean;
  type: "text" | "password" | "checkbox";
  /** Only shown when a condition field is truthy */
  conditionalOn?: string;
}

const CXONE_FIELDS: FieldDef[] = [
  { key: "accessKey", label: "Access Key", required: true, sensitive: false, type: "text" },
  { key: "secretKey", label: "Secret Key", required: true, sensitive: true, type: "password" },
  { key: "clientId", label: "Client ID", required: false, sensitive: false, type: "text" },
  { key: "clientSecret", label: "Client Secret", required: false, sensitive: true, type: "password" },
];

const AMAZON_CONNECT_FIELDS: FieldDef[] = [
  { key: "AmazonConnectARN", label: "Amazon Connect ARN", required: true, sensitive: false, type: "text" },
  { key: "RecordingBucketName", label: "Recording Bucket Name", required: true, sensitive: false, type: "text" },
  { key: "RecordingBucketRegion", label: "Recording Bucket Region", required: true, sensitive: false, type: "text" },
  { key: "CTRBucketName", label: "CTR Bucket Name", required: true, sensitive: false, type: "text" },
  { key: "CTRBucketRegion", label: "CTR Bucket Region", required: true, sensitive: false, type: "text" },
  { key: "CTRBucketPrefix", label: "CTR Bucket Prefix", required: true, sensitive: false, type: "text" },
  { key: "CallRecordingPrefix", label: "Call Recording Prefix", required: false, sensitive: false, type: "text" },
  { key: "ChatTranscriptPrefix", label: "Chat Transcript Prefix", required: false, sensitive: false, type: "text" },
  { key: "ScreenRecordingPrefix", label: "Screen Recording Prefix", required: false, sensitive: false, type: "text" },
  { key: "AccessRoleARN", label: "Access Role ARN", required: true, sensitive: false, type: "text" },
  { key: "ShouldUseExternalBucket", label: "Use External Bucket", required: false, sensitive: false, type: "checkbox" },
  { key: "RecordingDestinationBucketName", label: "Recording Destination Bucket Name", required: false, sensitive: false, type: "text", conditionalOn: "ShouldUseExternalBucket" },
  { key: "RecordingDestinationBucketRegion", label: "Recording Destination Region", required: false, sensitive: false, type: "text", conditionalOn: "ShouldUseExternalBucket" },
];

const FIVE9_FIELDS: FieldDef[] = [
  { key: "username", label: "Username", required: true, sensitive: false, type: "text" },
  { key: "password", label: "Password", required: true, sensitive: true, type: "password" },
  { key: "reportName", label: "Report Name", required: true, sensitive: false, type: "text" },
  { key: "folderName", label: "Folder Name", required: true, sensitive: false, type: "text" },
];

function getFieldsForType(type: string): FieldDef[] {
  switch (type) {
    case "CXone":
      return CXONE_FIELDS;
    case "AmazonConnect":
      return AMAZON_CONNECT_FIELDS;
    case "Five9":
      return FIVE9_FIELDS;
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Helpers for ConnectorSecret / plain values
// ---------------------------------------------------------------------------

function isConnectorSecret(val: unknown): val is ConnectorSecret {
  return (
    typeof val === "object" &&
    val !== null &&
    "hasValue" in val &&
    "isSensitive" in val
  );
}

/**
 * Build initial detail values for the flyout form from a connector row.
 * For secrets with hasValue=true we leave value empty (masked).
 * For plain strings we keep the value.
 */
function buildDetailsFromRow(
  row: IConnectorRow,
  fields: FieldDef[],
): Record<string, string | boolean> {
  const result: Record<string, string | boolean> = {};
  const details = row.connectorDetails ?? {};

  // Map backend PascalCase keys to form keys
  for (const field of fields) {
    const backendKey = findBackendKey(details, field.key);
    const val = backendKey ? details[backendKey] : undefined;

    if (field.type === "checkbox") {
      result[field.key] = typeof val === "boolean" ? val : false;
    } else if (isConnectorSecret(val)) {
      // Sensitive field — leave value empty, we track masking separately
      result[field.key] = val.value ?? "";
    } else if (typeof val === "string") {
      result[field.key] = val;
    } else {
      result[field.key] = "";
    }
  }
  return result;
}

/**
 * Determine which detail keys are masked (have a stored value on the server
 * but we don't expose it).
 */
function buildMaskedKeys(
  row: IConnectorRow,
  fields: FieldDef[],
): Set<string> {
  const masked = new Set<string>();
  const details = row.connectorDetails ?? {};
  for (const field of fields) {
    const backendKey = findBackendKey(details, field.key);
    const val = backendKey ? details[backendKey] : undefined;
    if (isConnectorSecret(val) && val.hasValue && val.isSensitive) {
      masked.add(field.key);
    }
  }
  return masked;
}

/**
 * Case-insensitive match for backend keys (backend uses PascalCase, form may
 * use camelCase).
 */
function findBackendKey(
  details: Record<string, unknown>,
  formKey: string,
): string | undefined {
  if (formKey in details) return formKey;
  const lower = formKey.toLowerCase();
  return Object.keys(details).find((k) => k.toLowerCase() === lower);
}

const sectionHeaderSx = {
  fontSize: "0.8125rem",
  fontWeight: 700,
  color: "var(--text-primary)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  pb: "8px",
  borderBottom: "1px solid var(--border-subtle)",
  mb: "12px",
};

// ---------------------------------------------------------------------------
// Rule-set updater helpers (extracted to module scope to reduce nesting depth)
// ---------------------------------------------------------------------------

type FormRuleSet = { _id: string; siteId: string; rules: (ConnectorRule & { _id: string })[] };

function removeRuleFromRuleSets(
  ruleSets: FormRuleSet[],
  rsIndex: number,
  ruleIndex: number,
): FormRuleSet[] {
  return ruleSets.map((rs, i) =>
    i === rsIndex
      ? { ...rs, rules: rs.rules.filter((_, ri) => ri !== ruleIndex) }
      : rs,
  );
}

function updateRuleInRuleSets(
  ruleSets: FormRuleSet[],
  rsIndex: number,
  ruleIndex: number,
  field: "attributeName" | "attributeValue",
  value: string,
): FormRuleSet[] {
  return ruleSets.map((rs, i) =>
    i === rsIndex
      ? {
          ...rs,
          rules: rs.rules.map((r, ri) =>
            ri === ruleIndex ? { ...r, [field]: value } : r,
          ),
        }
      : rs,
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function V2Connectors() {
  const { t } = useTranslation(["connectors", "common", "errors", "grid"]);
  const formatInterval = makeFormatInterval(t as (key: string, opts?: Record<string, unknown>) => string);
  const { customerId } = useParams<{ customerId: string }>();
  const { user: authUser } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // -- Data -----------------------------------------------------------------
  const { data: connectorsData, isLoading: connectorsLoading } =
    useGetConnectors(customerId);
  const connectors: IConnectorRow[] = connectorsData?.connectors ?? [];

  const { data: sitesData } = useSites(customerId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sites: Site[] = sitesData?.sites ?? [];
  const sitesMap = useMemo(() => {
    const m = new Map<string, Site>();
    for (const s of sites) m.set(s.siteId, s);
    return m;
  }, [sites]);

  // -- Mutations ------------------------------------------------------------
  const createMutation = useCreateConnector();
  const editMutation = useEditConnector();
  const deleteMutation = useDeleteConnector({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getConnectors", customerId] });
      setFlyoutOpen(false);
      setSelectedId(null);
      resetForm();
    },
  });

  // -- Selection & flyout ---------------------------------------------------
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedConnector =
    connectors.find((c) => c.connectorId === selectedId) ?? null;

  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutMode, setFlyoutMode] = useState<"create" | "edit">("create");

  // -- Permissions ----------------------------------------------------------
  const canCreate = authUser?.hasPermission("connectors:Create") ?? false;
  const canEdit = authUser?.hasPermission("connectors:Update") ?? false;
  const canDelete = authUser?.hasPermission("connectors:Delete") ?? false;

  // -- Form state -----------------------------------------------------------
  const [formType, setFormType] = useState<string>("");
  const [formInterval, setFormInterval] = useState<number>(86_400);
  const [formSaveNoMedia, setFormSaveNoMedia] = useState(false);
  const [formDetails, setFormDetails] = useState<Record<string, string | boolean>>({});
  const [formRuleSets, setFormRuleSets] = useState<FormRuleSet[]>(
    [{ _id: crypto.randomUUID(), siteId: "", rules: [] }]
  );
  const [formError, setFormError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /** Keys where the server has a stored value but we show masked */
  const [maskedKeys, setMaskedKeys] = useState<Set<string>>(new Set());
  /** Keys the user has chosen to edit (unmask) */
  const [editingKeys, setEditingKeys] = useState<Set<string>>(new Set());

  // -- Reset form -----------------------------------------------------------
  const resetForm = () => {
    setFormType("");
    setFormInterval(86_400);
    setFormSaveNoMedia(false);
    setFormDetails({});
    setFormRuleSets([{ _id: crypto.randomUUID(), siteId: "", rules: [] }]);
    setFormError("");
    setShowDeleteConfirm(false);
    setMaskedKeys(new Set());
    setEditingKeys(new Set());
  };

  // -- Populate form from connector -----------------------------------------
  const populateFromConnector = (conn: IConnectorRow) => {
    const fields = getFieldsForType(conn.type);
    setFormType(conn.type);
    setFormInterval(conn.intervalSeconds || 86_400);
    setFormSaveNoMedia(conn.shouldSaveNoMedia);
    setFormDetails(buildDetailsFromRow(conn, fields));
    setFormRuleSets(
      conn.ruleSets.length > 0
        ? conn.ruleSets.map((rs) => ({
            _id: crypto.randomUUID(),
            siteId: rs.siteId ?? rs.site?.siteId ?? "",
            rules: (rs.rules ?? []).map((r) => ({ ...r, _id: crypto.randomUUID() })),
          }))
        : [{ _id: crypto.randomUUID(), siteId: "", rules: [] }],
    );
    setMaskedKeys(buildMaskedKeys(conn, fields));
    setEditingKeys(new Set());
    setFormError("");
    setShowDeleteConfirm(false);
  };

  // -- Handlers -------------------------------------------------------------
  const handleRowSelect = (connectorId: string) => {
    if (!canEdit) {
      setSelectedId((prev) => (prev === connectorId ? null : connectorId));
      return;
    }
    const conn = connectors.find((c) => c.connectorId === connectorId);
    if (!conn) return;

    if (selectedId === connectorId && flyoutOpen) {
      setFlyoutOpen(false);
      setSelectedId(null);
      return;
    }

    setSelectedId(connectorId);
    setFlyoutMode("edit");
    setFlyoutOpen(true);
    populateFromConnector(conn);
  };

  useEffect(() => {
    if (flyoutOpen && flyoutMode === "edit" && selectedConnector) {
      populateFromConnector(selectedConnector);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnector?.connectorId]);

  const handleCreate = () => {
    setSelectedId(null);
    setFlyoutMode("create");
    setFlyoutOpen(true);
    resetForm();
  };

  const handleCloseFlyout = () => {
    setFlyoutOpen(false);
    resetForm();
  };

  // -- When type changes, rebuild details -----------------------------------
  const handleTypeChange = (newType: string) => {
    setFormType(newType);
    // Reset details to defaults for new type
    const fields = getFieldsForType(newType);
    const defaults: Record<string, string | boolean> = {};
    for (const f of fields) {
      defaults[f.key] = f.type === "checkbox" ? false : "";
    }
    setFormDetails(defaults);
    setMaskedKeys(new Set());
    setEditingKeys(new Set());
  };

  const handleDetailChange = (key: string, value: string | boolean) => {
    setFormDetails((prev) => ({ ...prev, [key]: value }));
  };

  // -- Rule set handlers ----------------------------------------------------
  const handleAddRuleSet = () => {
    setFormRuleSets((prev) => [...prev, { _id: crypto.randomUUID(), siteId: "", rules: [] }]);
  };

  const handleRemoveRuleSet = (index: number) => {
    setFormRuleSets((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRuleSetSiteChange = (index: number, siteId: string) => {
    setFormRuleSets((prev) =>
      prev.map((rs, i) => (i === index ? { ...rs, siteId } : rs)),
    );
  };

  const handleAddRule = (rsIndex: number) => {
    setFormRuleSets((prev) =>
      prev.map((rs, i) =>
        i === rsIndex
          ? { ...rs, rules: [...rs.rules, { _id: crypto.randomUUID(), attributeName: "", attributeValue: "" }] }
          : rs,
      ),
    );
  };

  const handleRemoveRule = (rsIndex: number, ruleIndex: number) => {
    setFormRuleSets((prev) => removeRuleFromRuleSets(prev, rsIndex, ruleIndex));
  };

  const handleRuleChange = (
    rsIndex: number,
    ruleIndex: number,
    field: "attributeName" | "attributeValue",
    value: string,
  ) => {
    setFormRuleSets((prev) => updateRuleInRuleSets(prev, rsIndex, ruleIndex, field, value));
  };

  // -- Build the connector details for the API request ----------------------
  function buildConnectorDetailsPayload(): Record<string, unknown> {
    const fields = getFieldsForType(formType);
    const payload: Record<string, unknown> = {};

    for (const field of fields) {
      const val = formDetails[field.key];

      if (field.sensitive && maskedKeys.has(field.key) && !editingKeys.has(field.key)) {
        // Field is masked and user hasn't edited it — send ConnectorSecret with empty value
        payload[field.key] = { value: "", isSensitive: true, hasValue: true };
      } else if (field.sensitive) {
        payload[field.key] = {
          value: typeof val === "string" ? val : "",
          isSensitive: true,
          hasValue: typeof val === "string" && val.length > 0,
        };
      } else {
        payload[field.key] = val;
      }
    }
    return payload;
  }

  // -- Save -----------------------------------------------------------------
  const handleSave = () => { // NOSONAR
    // Validation
    if (!formType) {
      setFormError(t("connectors:typeRequired"));
      return;
    }
    if (!formInterval) {
      setFormError(t("connectors:intervalRequired"));
      return;
    }

    // Validate required detail fields
    const fields = getFieldsForType(formType);
    for (const field of fields) {
      if (!field.required) continue;
      if (field.type === "checkbox") continue;

      // In edit mode, masked fields that haven't been edited are valid
      if (maskedKeys.has(field.key) && !editingKeys.has(field.key)) continue;

      const val = formDetails[field.key];
      if (!val || (typeof val === "string" && !val.trim())) {
        setFormError(`${field.label} is required.`);
        return;
      }
    }

    // Validate rule sets
    const validRuleSets = formRuleSets.filter((rs) => rs.siteId);
    if (validRuleSets.length === 0) {
      setFormError(t("connectors:atLeastOneSiteMapping"));
      return;
    }

    setFormError("");

    const detailsPayload = buildConnectorDetailsPayload();

    const ruleSetsPayload: ConnectorRuleSet[] = validRuleSets.map((rs) => ({
      site: { siteId: rs.siteId } as Site,
      siteId: rs.siteId,
      rules: rs.rules.filter((r) => r.attributeName && r.attributeValue),
    }));

    if (flyoutMode === "create") {
      createMutation.mutate(
        {
          customerId: customerId!,
          type: formType,
          intervalSeconds: formInterval,
          shouldSaveNoMedia: formSaveNoMedia,
          [formType]: detailsPayload,
          ruleSets: ruleSetsPayload,
        } as any,
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["getConnectors", customerId],
            });
            setFlyoutOpen(false);
            resetForm();
          },
          onError: (err: any) =>
            setFormError(err?.message || t("connectors:failedToCreateConnector")),
        },
      );
    } else {
      if (!selectedConnector) return;
      editMutation.mutate(
        {
          customerId: customerId!,
          connectorId: selectedConnector.connectorId,
          type: formType,
          intervalSeconds: formInterval,
          shouldSaveNoMedia: formSaveNoMedia,
          [formType]: detailsPayload,
          ruleSets: ruleSetsPayload,
        } as any,
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["getConnectors", customerId],
            });
            setFlyoutOpen(false);
            resetForm();
          },
          onError: (err: any) =>
            setFormError(err?.message || t("connectors:failedToUpdateConnector")),
        },
      );
    }
  };

  const handleDelete = () => {
    if (!selectedConnector || !customerId) return;
    deleteMutation.mutate({
      customerId,
      connectorId: selectedConnector.connectorId,
      jobConfigId: selectedConnector.jobConfigId,
      type: selectedConnector.type,
    });
  };

  const isSaving = createMutation.isPending || editMutation.isPending;

  // -- Helpers for rendering the current type's fields ----------------------
  const currentFields = getFieldsForType(formType);

  // -- Save button label (avoids nested ternary in JSX) --------------------
  const savingLabel = flyoutMode === "create"
    ? t("connectors:creatingConnector")
    : t("connectors:savingConnector");
  const saveButtonLabel = isSaving
    ? savingLabel
    : flyoutMode === "create"
      ? t("connectors:createConnector")
      : t("common:saveChanges");

  // -- Render helpers -------------------------------------------------------
  const connectorListEmptyOrTable = connectors.length === 0 ? (
    <EmptyState message={t("connectors:noConnectorsFound")} />
  ) : (
    <Box component="table" sx={crudTableSx}>
      <thead>
        <tr>
          <Box component="th" sx={{ width: 40, textAlign: "center" }} />
          <th>{t("connectors:type")}</th>
          <th>{t("connectors:interval")}</th>
          <th>{t("common:sites")}</th>
          <th>{t("grid:saveNoMedia")}</th>
        </tr>
      </thead>
      <tbody>
        {connectors.map((c) => {
          const isSelected = selectedId === c.connectorId;
          const siteNames = c.ruleSets
            .map((rs) => {
              const sid = rs.siteId ?? rs.site?.siteId;
              return sid ? sitesMap.get(sid)?.name ?? sid : "—";
            })
            .join(", ");

          return (
            <Box
              component="tr"
              key={c.connectorId}
              onClick={() => handleRowSelect(c.connectorId)}
              sx={selectedRowSx(isSelected)}
            >
              <Box component="td" sx={{ textAlign: "center" }}>
                <Box
                  component="input"
                  type="radio"
                  name="connector-select"
                  checked={isSelected}
                  onChange={() => handleRowSelect(c.connectorId)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={radioInputSx}
                />
              </Box>
              <Box component="td" sx={primaryCellSx}>
                {c.type}
              </Box>
              <td>{formatInterval(c.intervalSeconds)}</td>
              <td>
                {c.ruleSets.length > 0 ? siteNames : "—"}
              </td>
              <td>
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    px: "8px",
                    py: "2px",
                    borderRadius: "4px",
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    backgroundColor: c.shouldSaveNoMedia
                      ? "rgba(6,214,160,0.12)"
                      : "rgba(148,163,184,0.12)",
                    color: c.shouldSaveNoMedia
                      ? "var(--accent)"
                      : "var(--text-secondary)",
                  }}
                >
                  {c.shouldSaveNoMedia ? t("connectors:yes") : t("connectors:no")}
                </Box>
              </td>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );

  // -- Render ---------------------------------------------------------------

  const flyoutTitle = flyoutMode === "create" ? t("connectors:createConnector") : t("connectors:editConnector");

  return (
    <CrudPageLayout
      toolbar={
        canCreate ? (
          <Box component="button" onClick={handleCreate} sx={toolbarBtnSx(false)}>
            <PlusIcon />
            {t("connectors:createConnector")}
          </Box>
        ) : null
      }
      extra={
        <FlyoutDrawer
          open={flyoutOpen}
          onClose={handleCloseFlyout}
          title={flyoutTitle}
          footer={
            <FlyoutFooter
              onCancel={handleCloseFlyout}
              cancelLabel={t("common:cancel")}
              onSave={handleSave}
              saveLabel={saveButtonLabel}
              isSaving={isSaving}
              showDelete={flyoutMode === "edit" && canDelete && !showDeleteConfirm}
              onDelete={() => setShowDeleteConfirm(true)}
              deleteLabel={t("connectors:deleteConnector")}
            />
          }
        >
          {formError && (
            <Box sx={{ color: "#FB7185", fontSize: "0.8125rem" }}>
              {formError}
            </Box>
          )}

            {/* ===== Section: Basic Config ===== */}
            <Box sx={sectionHeaderSx}>{t("connectors:basicConfiguration")}</Box>

            {/* Type */}
            <Box>
              <Box sx={flyoutLabelSx}>{t("connectors:type")} *</Box>
              <Box
                component="select"
                value={formType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleTypeChange(e.target.value)
                }
                disabled={flyoutMode === "edit"}
                sx={{
                  ...flyoutSelectSx,
                  opacity: flyoutMode === "edit" ? 0.6 : 1,
                }}
              >
                <option value="">{t("connectors:selectConnectorType")}</option>
                {CONNECTOR_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Box>
            </Box>

            {/* Interval */}
            <Box>
              <Box sx={flyoutLabelSx}>{t("connectors:interval")} *</Box>
              <Box
                component="select"
                value={formInterval}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setFormInterval(Number(e.target.value))
                }
                sx={flyoutSelectSx}
              >
                {INTERVAL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Box>
            </Box>

            {/* Save No Media */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <Box
                component="input"
                type="checkbox"
                checked={formSaveNoMedia}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormSaveNoMedia(e.target.checked)
                }
                sx={{
                  accentColor: "var(--accent)",
                  width: 16,
                  height: 16,
                  cursor: "pointer",
                }}
              />
              <Box
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "var(--text-secondary)",
                }}
              >
                {t("connectors:saveNoMedia")}
              </Box>
            </Box>

            {/* ===== Section: Connector Details ===== */}
            {formType && (
              <>
                <Box sx={{ ...sectionHeaderSx, mt: "8px" }}>
                  {t("connectors:configuration", { type: formType })}
                </Box>

                {currentFields.map((field) => {
                  // Conditional visibility
                  if (
                    field.conditionalOn &&
                    !formDetails[field.conditionalOn]
                  ) {
                    return null;
                  }

                  // Checkbox fields
                  if (field.type === "checkbox") {
                    return (
                      <Box
                        key={field.key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <Box
                          component="input"
                          type="checkbox"
                          checked={!!formDetails[field.key]}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) =>
                            handleDetailChange(field.key, e.target.checked)
                          }
                          sx={{
                            accentColor: "var(--accent)",
                            width: 16,
                            height: 16,
                            cursor: "pointer",
                          }}
                        />
                        <Box
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: 600,
                            color: "var(--text-secondary)",
                          }}
                        >
                          {field.label}
                        </Box>
                      </Box>
                    );
                  }

                  // Masked sensitive field in edit mode
                  const isMasked =
                    maskedKeys.has(field.key) && !editingKeys.has(field.key);

                  return (
                    <Box key={field.key}>
                      <Box sx={flyoutLabelSx}>
                        {field.label}
                        {field.required ? " *" : ""}
                      </Box>
                      {isMasked ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              padding: "10px 12px",
                              borderRadius: "8px",
                              border: "1px solid var(--border-mid)",
                              backgroundColor: "var(--bg-raised)",
                              color: "var(--text-tertiary)",
                              fontSize: "0.875rem",
                              fontFamily: "inherit",
                              letterSpacing: "0.12em",
                            }}
                          >
                            ••••••••
                          </Box>
                          <Box
                            component="button"
                            onClick={() =>
                              setEditingKeys(
                                (prev) => new Set([...prev, field.key]),
                              )
                            }
                            sx={{
                              px: "10px",
                              py: "8px",
                              borderRadius: "6px",
                              border: "1px solid var(--border-mid)",
                              bgcolor: "var(--bg-raised)",
                              color: "var(--text-secondary)",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              whiteSpace: "nowrap",
                              "&:hover": {
                                bgcolor: "var(--bg-hover)",
                                color: "var(--text-primary)",
                              },
                            }}
                          >
                            {t("connectors:edit")}
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          component="input"
                          type={field.type === "password" ? "password" : "text"}
                          placeholder={field.label}
                          value={
                            typeof formDetails[field.key] === "string"
                              ? (formDetails[field.key] as string)
                              : ""
                          }
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => handleDetailChange(field.key, e.target.value)}
                          sx={flyoutInputSx}
                        />
                      )}
                    </Box>
                  );
                })}
              </>
            )}

            {/* ===== Section: Rule Sets ===== */}
            <Box sx={{ ...sectionHeaderSx, mt: "8px" }}>
              {t("connectors:siteMappings")} *
            </Box>

            {formRuleSets.map((rs, rsIndex) => (
              <Box
                key={rs._id}
                sx={{
                  border: "1px solid var(--border-subtle)",
                  borderRadius: "8px",
                  p: "12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={flyoutLabelSx}>{t("common:site")}</Box>
                  {formRuleSets.length > 1 && (
                    <Box
                      component="button"
                      onClick={() => handleRemoveRuleSet(rsIndex)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        border: "none",
                        bgcolor: "transparent",
                        color: "#FB7185",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        "&:hover": { color: "#FCA5A5" },
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        style={{ width: 12, height: 12, marginRight: 4 }}
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      {t("connectors:remove")}
                    </Box>
                  )}
                </Box>

                <Box
                  component="select"
                  value={rs.siteId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleRuleSetSiteChange(rsIndex, e.target.value)
                  }
                  sx={flyoutSelectSx}
                >
                  <option value="">{t("connectors:selectSite")}</option>
                  {sites.map((s) => (
                    <option key={s.siteId} value={s.siteId}>
                      {s.name}
                    </option>
                  ))}
                </Box>

                {/* Rules within this rule set */}
                {rs.rules.length > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: "4px" }}>
                    <Box sx={{ ...flyoutLabelSx, fontSize: "0.6875rem" }}>
                      {t("connectors:rules")}
                    </Box>
                    {rs.rules.map((rule, ruleIdx) => (
                      <Box
                        key={rule._id}
                        sx={{ display: "flex", gap: "6px", alignItems: "center" }}
                      >
                        <Box
                          component="input"
                          type="text"
                          placeholder={t("connectors:attributeName")}
                          value={rule.attributeName}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleRuleChange(rsIndex, ruleIdx, "attributeName", e.target.value)
                          }
                          sx={{ ...flyoutInputSx, flex: 1, padding: "6px 8px", fontSize: "0.8125rem" }}
                        />
                        <Box
                          component="input"
                          type="text"
                          placeholder={t("connectors:attributeValue")}
                          value={rule.attributeValue}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleRuleChange(rsIndex, ruleIdx, "attributeValue", e.target.value)
                          }
                          sx={{ ...flyoutInputSx, flex: 1, padding: "6px 8px", fontSize: "0.8125rem" }}
                        />
                        <Box
                          component="button"
                          onClick={() => handleRemoveRule(rsIndex, ruleIdx)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            borderRadius: "4px",
                            border: "none",
                            bgcolor: "transparent",
                            color: "#FB7185",
                            cursor: "pointer",
                            flexShrink: 0,
                            "&:hover": { bgcolor: "rgba(251,113,133,0.1)" },
                          }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            style={{ width: 12, height: 12 }}
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box
                  component="button"
                  onClick={() => handleAddRule(rsIndex)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    border: "none",
                    bgcolor: "transparent",
                    color: "var(--accent)",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    p: 0,
                    "&:hover": { opacity: 0.8 },
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    style={{ width: 12, height: 12 }}
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  {t("connectors:addRule")}
                </Box>
              </Box>
            ))}

            <Box
              component="button"
              onClick={handleAddRuleSet}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                px: "12px",
                py: "6px",
                borderRadius: "6px",
                border: "1px dashed var(--border-mid)",
                bgcolor: "transparent",
                color: "var(--accent)",
                fontSize: "0.8125rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": { bgcolor: "var(--bg-hover)" },
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                style={{ width: 14, height: 14 }}
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {t("connectors:addSiteMapping")}
            </Box>

            {/* Edit mode: Delete confirmation */}
            {flyoutMode === "edit" && showDeleteConfirm && selectedConnector && (
              <DeleteConfirmation
                message={t("connectors:deleteConnectorConfirm", { type: selectedConnector.type })}
                onCancel={() => setShowDeleteConfirm(false)}
                cancelLabel={t("common:cancel")}
                onConfirm={handleDelete}
                confirmLabel={t("connectors:confirmDelete")}
                isPending={deleteMutation.isPending}
                pendingLabel={t("common:deleting")}
              />
            )}
        </FlyoutDrawer>
      }
    >
      {connectorsLoading ? (
        <LoadingState message={t("connectors:loadingConnectors")} />
      ) : connectorListEmptyOrTable}
    </CrudPageLayout>
  );
}
